import { Divider, Text } from '@ui';
import { FormattedMessage, useIntl } from 'react-intl';

import { showIntercomMessenger } from '../../../iwIntercom/IntercomHelper';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { ChatBubblesSVG } from '../../../svgComponents/chatBubblesSVG/index';
import { EnvelopeSVG } from '../../../svgComponents/envelopeSVG';
import { FaqSVG } from '../../../svgComponents/faqSVG';
import { HelpSVG } from '../../../svgComponents/helpSVG';
import { PhoneSVG } from '../../../svgComponents/phoneSVG';

interface HelpModalContentProps {
    isIwocaPayLoan?: boolean;
    onChatButtonClick?: () => void;
}

export const HelpModalContent: React.FC<HelpModalContentProps> = ({
    isIwocaPayLoan = false,
    onChatButtonClick
}) => {
    const intl = useIntl();

    const callPhoneNumber = isIwocaPayLoan
        ? intl.formatMessage({
              id: '_.modal.help.iwocaPay.call_number',
              defaultMessage: '+44-0203-778-0549'
          })
        : intl.formatMessage({
              id: '_.modal.help.call_number',
              defaultMessage: '+44-020-3778-0105'
          });

    const emailAddress = isIwocaPayLoan
        ? intl.formatMessage({
              id: '_.modal.help.iwocaPay.email',
              defaultMessage: 'pay@iwoca.co.uk'
          })
        : intl.formatMessage({
              id: '_.modal.help.email',
              defaultMessage: 'support@iwoca.co.uk'
          });

    return (
        <div className="p-l">
            <div
                className="mb-l flex items-center justify-center"
                data-testid="help-modal"
            >
                <Text className="text-2xl font-med">
                    {isIwocaPayLoan ? (
                        <FormattedMessage
                            id="_.modal.help.iwocaPay.title"
                            defaultMessage="Need help?"
                        />
                    ) : (
                        <FormattedMessage
                            id="_.modal.help.title"
                            defaultMessage="Need help?"
                        />
                    )}
                </Text>
                <HelpSVG />
            </div>
            <div className="mb-xl text-center">
                {isIwocaPayLoan ? (
                    <FormattedMessage
                        id="_.modal.help.iwocaPay.availability_message_line_one"
                        defaultMessage="Monday to Friday"
                    />
                ) : (
                    <FormattedMessage
                        id="_.modal.help.availability_message_line_one"
                        defaultMessage="Monday to Friday"
                    />
                )}{' '}
                {isIwocaPayLoan ? (
                    <FormattedMessage
                        id="_.modal.help.iwocaPay.availability_message_line_two"
                        defaultMessage="9am - 6pm"
                    />
                ) : (
                    <FormattedMessage
                        id="_.modal.help.availability_message_line_two"
                        defaultMessage="9am - 6pm"
                    />
                )}
                {getCountry() === 'UK' && (
                    <Text className="text-s">(excluding bank holidays)</Text>
                )}
            </div>
            <div className="space-y-m">
                <div className="flex items-center">
                    <ChatBubblesSVG className="mr-s fill-core-65" />
                    <button
                        onClick={() => {
                            showIntercomMessenger();
                            onChatButtonClick?.();
                        }}
                        className="border-none bg-transparent text-primary-40 underline"
                    >
                        {isIwocaPayLoan ? (
                            <FormattedMessage
                                id="_.modal.help.iwocaPay.chat"
                                defaultMessage="Chat to us"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.modal.help.chat"
                                defaultMessage="Chat to us"
                            />
                        )}
                    </button>
                </div>
                <div className="flex items-center">
                    <PhoneSVG className="mr-s fill-core-65" />
                    <div>
                        {isIwocaPayLoan ? (
                            <FormattedMessage
                                id="_.modal.help.iwocaPay.phone_caption"
                                defaultMessage="Call us at"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.modal.help.phone_caption"
                                defaultMessage="Call us on"
                            />
                        )}{' '}
                        <a
                            href={`tel:${callPhoneNumber}`}
                            className="text-primary-40 underline"
                        >
                            {isIwocaPayLoan ? (
                                <FormattedMessage
                                    id="_.modal.help.iwocaPay.display_number"
                                    defaultMessage="0203 778 0549"
                                />
                            ) : (
                                <FormattedMessage
                                    id="_.modal.help.display_number"
                                    defaultMessage="020 3778 0105"
                                />
                            )}
                        </a>
                    </div>
                </div>
                <div className="flex items-center">
                    <EnvelopeSVG className="mr-s fill-core-65" />
                    <div>
                        {isIwocaPayLoan ? (
                            <FormattedMessage
                                id="_.modal.help.iwocaPay.email_caption"
                                defaultMessage="Email at"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.modal.help.email_caption"
                                defaultMessage="Email us at"
                            />
                        )}{' '}
                        <a
                            href={`mailto:${emailAddress}`}
                            className="text-primary-40 underline"
                        >
                            {isIwocaPayLoan ? (
                                <FormattedMessage
                                    id="_.modal.help.iwocaPay.email"
                                    defaultMessage="pay@iwoca.co.uk"
                                />
                            ) : (
                                <FormattedMessage
                                    id="_.modal.help.email"
                                    defaultMessage="support@iwoca.co.uk"
                                />
                            )}
                        </a>
                    </div>
                </div>
            </div>
            <Divider className="my-xl" />
            <div className="flex items-center">
                <FaqSVG className="mr-s shrink-0 fill-core-65" />
                <div>
                    {isIwocaPayLoan ? (
                        <FormattedMessage
                            id="_.modal.help.iwocaPay.faq_caption"
                            defaultMessage="Find answers to common questions in our"
                        />
                    ) : (
                        <FormattedMessage
                            id="_.modal.help.faq_caption"
                            defaultMessage="Find answers to common questions in our"
                        />
                    )}{' '}
                    <a
                        href="/faq/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-40 underline"
                    >
                        {isIwocaPayLoan ? (
                            <FormattedMessage
                                id="_.modal.help.iwocaPay.faq"
                                defaultMessage="FAQ"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.modal.help.faq"
                                defaultMessage="FAQs"
                            />
                        )}
                    </a>
                </div>
            </div>
        </div>
    );
};
